'use client';

import { c as _c } from "react/compiler-runtime";
import { useGetUserActivityField } from '@morfi/backend-sdk-v2/client';
import { useLocale, useTranslations } from 'next-intl';
import type { Ref } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ProposalShareButton } from '@/components/launchpad/proposal-share-button';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { TradeDirectionMarker } from '@/components/market/identity/trade-direction-marker';
import { Skeleton } from '@/components/ui/app-skeleton';
import { DataListItem, type DataListItemMetric } from '@/components/ui/data-list-item';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { ArrowDownLeft01Icon, FlashIcon, GiftIcon, ShoppingCart01Icon, SparklesIcon, StarIcon } from '@/components/ui/icon/glyphs';
import { OutcomePill } from '@/components/ui/outcome-pill';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { useFormattedCurrency } from '@/lib/currency';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { useMarketDetailPrefetchIntent } from '@/lib/sdk/prefetch';
import { cn } from '@/lib/ui/utils';
import { ActivityShareButton } from './activity-share-button';
const LEADING_CONTAINER_CLASS = 'size-12 shrink-0 rounded-control' as const;
type ActivityKind = 'trade' | 'claim' | 'claim_backer_fees' | 'back' | 'unback' | 'boost' | 'gift_received' | 'gift_sent';
type TradeSide = 'buy' | 'sell';
type DirectionTone = 'credit' | 'debit' | 'neutral';
function truncatePubkey(pubkey: string | null | undefined): string {
  if (typeof pubkey !== 'string' || pubkey.length === 0) return '';
  if (pubkey.length <= 8) return pubkey;
  return `${pubkey.slice(0, 4)}…${pubkey.slice(-4)}`;
}
const DIRECTION_TONE_CLASS: Readonly<Record<DirectionTone, string>> = {
  credit: 'text-data-profit',
  debit: 'text-data-loss',
  neutral: 'text-foreground-secondary'
};
interface RowVisualSpec {
  glyph: IconSvgElement;
  iconTint: string;
  tone: DirectionTone;
}
function getVisualSpec(kind: ActivityKind, side: TradeSide | null): RowVisualSpec {
  switch (kind) {
    case 'trade':
      if (side === 'sell') {
        return {
          glyph: ArrowDownLeft01Icon,
          iconTint: 'text-trade-sell',
          tone: 'debit'
        };
      }
      return {
        glyph: ShoppingCart01Icon,
        iconTint: 'text-trade-buy',
        tone: 'credit'
      };
    case 'claim':
      return {
        glyph: SparklesIcon,
        iconTint: 'text-feedback-success-content',
        tone: 'credit'
      };
    case 'claim_backer_fees':
      return {
        glyph: SparklesIcon,
        iconTint: 'text-feedback-success-content',
        tone: 'credit'
      };
    case 'back':
      return {
        glyph: StarIcon,
        iconTint: 'text-feedback-success-content',
        tone: 'credit'
      };
    case 'unback':
      return {
        glyph: StarIcon,
        iconTint: 'text-feedback-error-content',
        tone: 'debit'
      };
    case 'boost':
      return {
        glyph: FlashIcon,
        iconTint: 'text-feedback-warning-content',
        tone: 'neutral'
      };
    case 'gift_received':
      return {
        glyph: GiftIcon,
        iconTint: 'text-feedback-success-content',
        tone: 'credit'
      };
    case 'gift_sent':
      return {
        glyph: GiftIcon,
        iconTint: 'text-foreground-secondary',
        tone: 'debit'
      };
  }
}
interface LeadingVisualProps {
  marketPubkey: string | null;
  coverImageUrl: string | null;
  nsfw: boolean | null;
  spec: RowVisualSpec;
  side: TradeSide | null;
  kind: ActivityKind;
  marketType: MarketIdentityData['marketType'];
}
function LeadingVisual(t0) {
  const $ = _c(20);
  const {
    marketPubkey,
    coverImageUrl,
    nsfw,
    spec,
    side,
    kind,
    marketType
  } = t0;
  const direction = kind === "trade" ? side : null;
  let t1;
  if ($[0] !== direction) {
    t1 = direction === null ? null : <TradeDirectionMarker direction={direction} />;
    $[0] = direction;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const marker = t1;
  const hasMarketImage = marketPubkey !== null && marketPubkey.length > 0 && kind !== "gift_sent" && kind !== "gift_received";
  if (hasMarketImage && marketPubkey !== null) {
    const t2 = direction === null ? undefined : "[&>[data-origin]]:-top-1 [&>[data-origin]]:bottom-auto";
    const t3 = nsfw ?? false;
    let t4;
    if ($[2] !== coverImageUrl || $[3] !== marketPubkey || $[4] !== t3) {
      t4 = <OptimizedImage variant="thumbnail" entity={{
        kind: "market",
        pubkey: marketPubkey,
        category: "",
        coverImageUrl,
        nsfw: t3
      }} alt="" size="size-12" rounded="rounded-control" preview={false} className={LEADING_CONTAINER_CLASS} />;
      $[2] = coverImageUrl;
      $[3] = marketPubkey;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    let t5;
    if ($[6] !== marker || $[7] !== marketType || $[8] !== t2 || $[9] !== t4) {
      t5 = <ThumbWithOrigin marketType={marketType} className={t2}>{t4}{marker}</ThumbWithOrigin>;
      $[6] = marker;
      $[7] = marketType;
      $[8] = t2;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  let t2;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = cn(LEADING_CONTAINER_CLASS, "relative flex items-center justify-center bg-background-tertiary");
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = spec.glyph;
  let t4;
  if ($[12] !== spec.iconTint) {
    t4 = cn("size-6", spec.iconTint);
    $[12] = spec.iconTint;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== spec.glyph || $[15] !== t4) {
    t5 = <Icon icon={t3} className={t4} />;
    $[14] = spec.glyph;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== marker || $[18] !== t5) {
    t6 = <div className={t2} data-testid="activity-row-icon-square">{t5}{marker}</div>;
    $[17] = marker;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
interface PrimaryTitleProps {
  kind: ActivityKind;
  marketPubkey: string | null;
  marketHref: string | undefined;
  marketTitle: string | null;
  counterpartyHandle: string | null;
}
function PrimaryTitle(t0) {
  const $ = _c(11);
  const {
    kind,
    marketPubkey,
    marketHref,
    marketTitle,
    counterpartyHandle
  } = t0;
  const t = useTranslations("profile.stats");
  if (kind === "gift_received" || kind === "gift_sent") {
    let t1;
    if ($[0] !== counterpartyHandle || $[1] !== t) {
      t1 = counterpartyHandle !== null && counterpartyHandle.length > 0 ? `@${counterpartyHandle}` : t("activity_anonymous_morfian");
      $[0] = counterpartyHandle;
      $[1] = t;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    const label = t1;
    let t2;
    if ($[3] !== label) {
      t2 = <span className="truncate text-body font-medium text-foreground-primary" data-testid="activity-row-title-gift">{label}</span>;
      $[3] = label;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (marketHref !== undefined) {
    let t1;
    if ($[5] !== marketPubkey || $[6] !== marketTitle) {
      t1 = marketTitle !== null && marketTitle.length > 0 ? marketTitle : truncatePubkey(marketPubkey);
      $[5] = marketPubkey;
      $[6] = marketTitle;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    const label_0 = t1;
    let t2;
    if ($[8] !== label_0) {
      t2 = <span data-testid="activity-row-title-link">{label_0}</span>;
      $[8] = label_0;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t1;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="truncate text-body font-medium text-foreground-primary" data-testid="activity-row-title-fallback" />;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
}
interface SecondaryLineProps {
  kind: ActivityKind;
  timestamp: string;
  outcomeLabel: string | null;
  outcomeIndex: number | null;
}
function SecondaryLine(t0) {
  const $ = _c(25);
  const {
    kind,
    timestamp,
    outcomeLabel,
    outcomeIndex
  } = t0;
  const t = useTranslations("profile.stats");
  const tBacking = useTranslations("profile.next.pnl_card");
  const locale = useLocale();
  const tTimeIntl = useTranslations("time");
  let t1;
  if ($[0] !== tTimeIntl) {
    t1 = (key, params) => tTimeIntl(key as Parameters<typeof tTimeIntl>[0], params);
    $[0] = tTimeIntl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tTime = t1;
  let t2;
  if ($[2] !== locale || $[3] !== tTime || $[4] !== timestamp) {
    t2 = formatRelativeTime(timestamp, tTime, {
      style: "short",
      locale
    });
    $[2] = locale;
    $[3] = tTime;
    $[4] = timestamp;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const relative = t2;
  let t3;
  if ($[6] !== kind || $[7] !== t || $[8] !== tBacking) {
    t3 = kind === "gift_sent" ? t("activity_verb_gift_sent") : kind === "gift_received" ? t("activity_verb_gift_received") : kind === "unback" ? tBacking("withdraw") : null;
    $[6] = kind;
    $[7] = t;
    $[8] = tBacking;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const verb = t3;
  const hasOutcome = typeof outcomeLabel === "string" && outcomeLabel.length > 0 && typeof outcomeIndex === "number";
  const showTime = relative.length > 0;
  let t4;
  if ($[10] !== verb) {
    t4 = verb === null ? null : <span className="shrink-0 text-foreground-tertiary" data-testid="activity-row-kind">{verb}</span>;
    $[10] = verb;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== hasOutcome || $[13] !== outcomeIndex || $[14] !== outcomeLabel) {
    t5 = hasOutcome && <OutcomePill outcome={{
      title: outcomeLabel,
      index: outcomeIndex
    }} label={outcomeLabel} size="sm" labelLayout="natural" className="items-center py-0.5" />;
    $[12] = hasOutcome;
    $[13] = outcomeIndex;
    $[14] = outcomeLabel;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== hasOutcome || $[17] !== relative || $[18] !== showTime || $[19] !== verb) {
    t6 = showTime && <>{verb !== null || hasOutcome ? <span aria-hidden="true" className="shrink-0 text-foreground-tertiary">·</span> : null}<span className="shrink-0 tabular-nums text-foreground-tertiary" data-testid="activity-row-timestamp">{relative}</span></>;
    $[16] = hasOutcome;
    $[17] = relative;
    $[18] = showTime;
    $[19] = verb;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t7 = <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-caption text-foreground-secondary" data-testid="activity-row-secondary-line">{t4}{t5}{t6}</span>;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
export interface ActivityRowProps {
  /** Stable row id (chain signature or gift opaqueId) — drives field lookups. */
  rowId: string;
  /** Whether the viewed portfolio belongs to the signed-in user. */
  isOwn: boolean;
  /** Viewport-tracking ref forwarded from the SDK `<Items>` render prop. */
  ref?: Ref<HTMLLIElement>;
}

/**
 * Spec 088 + activity-feed restoration — single Recent Activity row.
 *
 * Rendered inside an `<Items>` render-prop from `useGetUserActivityInfinite`.
 * Each visible row reads its own fields off `useGetUserActivityField` and
 * switches its visual on `kind`. The 7 kinds are: `trade` (buy/sell),
 * `claim` (prize claim), `back` / `unback`, `boost`, `gift_sent`,
 * `gift_received`. Market title falls back to a truncated pubkey when the
 * SDK row doesn't carry one; the leading thumbnail is rendered via the
 * standard market-pubkey-derived `<OptimizedImage>` lookup.
 */
export function ActivityRow(props) {
  const $ = _c(68);
  const {
    rowId,
    isOwn,
    ref
  } = props;
  const tRewards = useTranslations("profile.rewards");
  const {
    formatD6
  } = useFormattedCurrency();
  const marketType = useGetUserActivityField(rowId, "marketType");
  const kind = (useGetUserActivityField(rowId, "kind") ?? "trade") as ActivityKind;
  const market = useGetUserActivityField(rowId, "market") as string | undefined ?? null;
  const amountD6 = useGetUserActivityField(rowId, "amountD6") as string | null ?? "0";
  const ts = useGetUserActivityField(rowId, "ts") as string | null ?? "";
  const side = useGetUserActivityField(rowId, "side") as TradeSide | undefined ?? null;
  const counterpartyHandle = useGetUserActivityField(rowId, "counterpartyHandle") as string | undefined ?? null;
  const trader = useGetUserActivityField(rowId, "trader") as string | undefined ?? null;
  const outcomeLabel = useGetUserActivityField(rowId, "outcomeLabel") as string | undefined ?? null;
  const outcomeIndex = useGetUserActivityField(rowId, "outcomeIndex") as number | undefined ?? null;
  const marketTitle = useGetUserActivityField(rowId, "marketTitle") as string | undefined ?? null;
  const marketCoverImageUrl = useGetUserActivityField(rowId, "marketCoverImageUrl") as string | undefined ?? null;
  const marketCoverImageNsfw = useGetUserActivityField(rowId, "marketCoverImageNsfw") as boolean | undefined ?? null;
  let t0;
  if ($[0] !== kind || $[1] !== side) {
    t0 = getVisualSpec(kind, side);
    $[0] = kind;
    $[1] = side;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const spec = t0;
  const isGift = kind === "gift_sent" || kind === "gift_received";
  const opensProposal = kind === "back" || kind === "unback";
  let t1;
  if ($[3] !== isGift || $[4] !== market || $[5] !== opensProposal) {
    t1 = marketHrefFor(market, isGift, opensProposal);
    $[3] = isGift;
    $[4] = market;
    $[5] = opensProposal;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const marketHref = t1;
  let t2;
  if ($[7] !== market || $[8] !== marketTitle) {
    t2 = marketTitle !== null && marketTitle.length > 0 ? marketTitle : truncatePubkey(market);
    $[7] = market;
    $[8] = marketTitle;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const titleLabel = t2;
  const dataWarm = useMarketDetailPrefetchIntent(isGift || opensProposal ? null : market);
  const intent = useIntentRoutePrefetch(marketHref, dataWarm.onMouseEnter);
  let action;
  if (kind === "trade" && side !== null && typeof outcomeIndex === "number" && market !== null && market.length > 0 && trader !== null && trader.length > 0) {
    let t3;
    if ($[10] !== amountD6 || $[11] !== market || $[12] !== outcomeIndex || $[13] !== rowId || $[14] !== side || $[15] !== trader) {
      t3 = <ActivityShareButton activityType="trade" signature={rowId} marketPubkey={market} traderPubkey={trader} direction={side} amountD6={amountD6} outcomeIndex={outcomeIndex} />;
      $[10] = amountD6;
      $[11] = market;
      $[12] = outcomeIndex;
      $[13] = rowId;
      $[14] = side;
      $[15] = trader;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    action = t3;
  } else {
    if (opensProposal && market !== null && market.length > 0) {
      let t3;
      if ($[17] !== market || $[18] !== titleLabel) {
        t3 = <ProposalShareButton proposalPubkey={market} proposalTitle={titleLabel} iconOnly={true} className="size-7 p-0" />;
        $[17] = market;
        $[18] = titleLabel;
        $[19] = t3;
      } else {
        t3 = $[19];
      }
      action = t3;
    } else {
      if (kind === "boost" && isOwn && market !== null && market.length > 0) {
        let t3;
        if ($[20] !== market || $[21] !== titleLabel) {
          t3 = <ActivityShareButton activityType="boost" marketPubkey={market} marketTitle={titleLabel} />;
          $[20] = market;
          $[21] = titleLabel;
          $[22] = t3;
        } else {
          t3 = $[22];
        }
        action = t3;
      }
    }
  }
  let t3;
  if ($[23] !== tRewards) {
    t3 = tRewards("amount");
    $[23] = tRewards;
    $[24] = t3;
  } else {
    t3 = $[24];
  }
  const t4 = DIRECTION_TONE_CLASS[spec.tone];
  let t5;
  if ($[25] !== amountD6 || $[26] !== formatD6) {
    t5 = formatD6(amountD6);
    $[25] = amountD6;
    $[26] = formatD6;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== t4 || $[29] !== t5) {
    t6 = <span className={t4} data-testid="activity-row-amount">{t5}</span>;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== t3 || $[32] !== t6) {
    t7 = {
      key: "amount",
      label: t3,
      value: t6
    };
    $[31] = t3;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  const amountMetric = t7;
  let t8;
  if ($[34] !== kind || $[35] !== market || $[36] !== marketCoverImageNsfw || $[37] !== marketCoverImageUrl || $[38] !== marketType || $[39] !== side || $[40] !== spec) {
    t8 = <LeadingVisual marketPubkey={market} coverImageUrl={marketCoverImageUrl} nsfw={marketCoverImageNsfw} spec={spec} side={side} kind={kind} marketType={marketType} />;
    $[34] = kind;
    $[35] = market;
    $[36] = marketCoverImageNsfw;
    $[37] = marketCoverImageUrl;
    $[38] = marketType;
    $[39] = side;
    $[40] = spec;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  let t9;
  if ($[42] !== counterpartyHandle || $[43] !== kind || $[44] !== market || $[45] !== marketHref || $[46] !== marketTitle) {
    t9 = <PrimaryTitle kind={kind} marketPubkey={market} marketHref={marketHref} marketTitle={marketTitle} counterpartyHandle={counterpartyHandle} />;
    $[42] = counterpartyHandle;
    $[43] = kind;
    $[44] = market;
    $[45] = marketHref;
    $[46] = marketTitle;
    $[47] = t9;
  } else {
    t9 = $[47];
  }
  let t10;
  if ($[48] !== kind || $[49] !== outcomeIndex || $[50] !== outcomeLabel || $[51] !== ts) {
    t10 = <SecondaryLine kind={kind} timestamp={ts} outcomeLabel={outcomeLabel} outcomeIndex={outcomeIndex} />;
    $[48] = kind;
    $[49] = outcomeIndex;
    $[50] = outcomeLabel;
    $[51] = ts;
    $[52] = t10;
  } else {
    t10 = $[52];
  }
  let t11;
  if ($[53] !== amountMetric) {
    t11 = [amountMetric];
    $[53] = amountMetric;
    $[54] = t11;
  } else {
    t11 = $[54];
  }
  let t12;
  if ($[55] !== action || $[56] !== amountMetric || $[57] !== intent || $[58] !== marketHref || $[59] !== t10 || $[60] !== t11 || $[61] !== t8 || $[62] !== t9 || $[63] !== titleLabel) {
    t12 = <DataListItem href={marketHref} intent={intent} ariaLabel={titleLabel} image={t8} primaryText={t9} secondaryText={t10} metrics={t11} compactMetric={amountMetric} action={action} />;
    $[55] = action;
    $[56] = amountMetric;
    $[57] = intent;
    $[58] = marketHref;
    $[59] = t10;
    $[60] = t11;
    $[61] = t8;
    $[62] = t9;
    $[63] = titleLabel;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  let t13;
  if ($[65] !== ref || $[66] !== t12) {
    t13 = <li ref={ref} data-testid="activity-row" className="mb-4 lg:mb-0">{t12}</li>;
    $[65] = ref;
    $[66] = t12;
    $[67] = t13;
  } else {
    t13 = $[67];
  }
  return t13;
}
function marketHrefFor(market: string | null, isGift: boolean, opensProposal: boolean): string | undefined {
  if (isGift || market === null || market.length === 0) return undefined;
  return opensProposal ? `/proposals/${market}` : `/markets/${market}`;
}
export function ActivityRowSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      key: "amount",
      label: <Skeleton className="h-4 w-12" />,
      value: <Skeleton className="h-5 w-16" />
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const amountMetric = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <li aria-hidden="true" data-testid="activity-row-skeleton" className="mb-4 lg:mb-0"><DataListItem href={undefined} ariaLabel="" image={<Skeleton className={LEADING_CONTAINER_CLASS} />} primaryText={<Skeleton className="h-5 w-48" />} secondaryText={<Skeleton className="h-4 w-32" />} metrics={[amountMetric]} compactMetric={amountMetric} /></li>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}